import { useLessonNavigation } from "@/lib/hooks/modal/useLessonNavigation";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import type { SlideFields } from "@/types/Slide";
import { LoadingSpinner, SearchBar } from "@ai-teach/ui-web";
import { useState } from "react";
import { HiDocumentText, HiFolder } from "react-icons/hi2";
import { MdChevronRight } from "react-icons/md";
import { ReferenceSlideThumbnail } from "./ReferenceSlideThumbnail";
import type { ReferenceSlide } from "./types";

const THUMBNAIL_WIDTH = 44;
const PICKED_THUMBNAIL_WIDTH = 80;

/**
 * Picks the one slide a section is modelled on — any slide in the library, not just one in the
 * project the generated lesson lands in. It is what tells the generator both the exact layout to
 * keep and what a good example of this exercise looks like.
 *
 * Opens at Home: a reference is as often from another series as from the project being worked in,
 * and browses down the same project → lesson → slide tree the move and duplicate modals use.
 */
export function ReferenceSlidePicker({
  selected,
  onSelect,
}: {
  selected: ReferenceSlide | null;
  onSelect: (slide: ReferenceSlide) => void;
}) {
  const [searchText, setSearchText] = useState("");

  const {
    projects,
    lessonsWithoutLimitation: lessons,
    slides,
    currentLessonId,
    navigationStack,
    isEmpty,
    projectLoading,
    lessonLoading,
    lessonSlidesLoading,
    hasMoreProjects,
    hasMoreLesson,
    handleProjectClick,
    handleLessonClick,
    navigateToIndex,
    loadMoreProjects,
    loadMoreLesson,
  } = useLessonNavigation(searchText, { includeSlides: true });

  const openLesson = lessons.find((one) => one.id === currentLessonId);
  const loading = projectLoading || lessonLoading || lessonSlidesLoading;

  const pickableSlides = slides.flatMap((slide) => [
    { slide, isSubslide: false },
    ...slide.children.map((child) => ({ slide: child, isSubslide: true })),
  ]);

  const pick = (slide: SlideFields) => {
    onSelect({
      id: slide.id,
      name: slide.name,
      template: slide.template,
      tagIds: slide.tagIds,
      slide,
    });
  };

  return (
    <div className="space-y-2">
      <SearchBar value={searchText} onChange={setSearchText} placeholder="Search…" />

      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => navigateToIndex(-1)}
          className={`shrink-0 font-medium transition-colors ${
            navigationStack.length === 0
              ? "cursor-default text-secondary-200"
              : "text-gray-400 hover:text-primary-500"
          }`}
        >
          Home
        </button>
        {navigationStack.map((item, index) => (
          <span key={item.id} className="flex min-w-0 items-center gap-1">
            <MdChevronRight className="h-4 w-4 shrink-0 text-gray-600" />
            <button
              type="button"
              onClick={() => navigateToIndex(index)}
              className="truncate font-medium text-gray-400 transition-colors hover:text-primary-500"
            >
              {item.name}
            </button>
          </span>
        ))}
        {openLesson && (
          <span className="flex min-w-0 items-center gap-1">
            <MdChevronRight className="h-4 w-4 shrink-0 text-gray-600" />
            <span className="truncate font-medium text-secondary-200">{openLesson.name}</span>
          </span>
        )}
      </nav>

      {/* The choice survives browsing away from it — a reference is picked once and then compared
          against others, and re-finding it after every detour is what makes this tedious. */}
      <div className="flex items-center gap-2.5 rounded-md border border-gray-200 px-3 py-2 dark:border-gray-800">
        {selected ? (
          <>
            <ReferenceSlideThumbnail slide={selected.slide} width={PICKED_THUMBNAIL_WIDTH} />
            <p className="min-w-0 truncate text-sm text-gray-400">
              Reference: <span className="font-semibold text-secondary-200">{selected.name}</span>
              <span className="ml-2 font-mono text-[11px]">
                {getTemplateLabel(selected.template)}
              </span>
            </p>
          </>
        ) : (
          <>
            <HiDocumentText className="h-4 w-4 shrink-0 text-primary-500" />
            <p className="text-sm text-gray-500">Open a lesson and pick a slide</p>
          </>
        )}
      </div>

      <div className="h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
        {loading && projects.length === 0 && lessons.length === 0 && pickableSlides.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4">
            <LoadingSpinner />
          </div>
        ) : isEmpty ? (
          <p className="p-4 text-sm text-gray-500">
            Nothing here. Search, or go back and open another project.
          </p>
        ) : currentLessonId ? (
          <ul>
            {pickableSlides.map(({ slide, isSubslide }) => (
              <li key={slide.id}>
                <button
                  type="button"
                  onClick={() => pick(slide)}
                  className={`flex w-full items-center gap-3 border-b border-gray-200 p-2.5 text-left transition-colors last:border-b-0 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-gray-800 ${
                    isSubslide ? "pl-8" : ""
                  } ${selected?.id === slide.id ? "bg-primary-500/10 ring-1 ring-primary-500" : ""}`}
                >
                  <ReferenceSlideThumbnail slide={slide} width={THUMBNAIL_WIDTH} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-secondary-200">{slide.name}</span>
                    <span className="block font-mono text-[11px] text-gray-500">
                      {getTemplateLabel(slide.template)}
                      {isSubslide ? " · subslide" : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <>
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => handleProjectClick({ id: project.id, name: project.name })}
                className="flex w-full items-center justify-between border-b border-gray-200 p-3 transition-colors last:border-b-0 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <HiFolder className="h-5 w-5 shrink-0 text-primary-500" />
                  <span className="truncate text-sm text-secondary-200">{project.name}</span>
                </span>
                <MdChevronRight className="h-5 w-5 shrink-0 text-secondary-200" />
              </button>
            ))}
            {hasMoreProjects && !projectLoading && (
              <button
                type="button"
                onClick={loadMoreProjects}
                className="w-full p-3 text-center text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Load more projects…
              </button>
            )}
            {lessons.map((lesson) => (
              <button
                key={lesson.id}
                type="button"
                onClick={() => handleLessonClick({ id: lesson.id, name: lesson.name })}
                className="flex w-full items-center justify-between border-b border-gray-200 p-3 transition-colors last:border-b-0 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <HiDocumentText className="h-5 w-5 shrink-0 text-secondary-200" />
                  <span className="truncate text-sm text-secondary-200">{lesson.name}</span>
                </span>
                <MdChevronRight className="h-5 w-5 shrink-0 text-secondary-200" />
              </button>
            ))}
            {hasMoreLesson && !lessonLoading && (
              <button
                type="button"
                onClick={loadMoreLesson}
                className="w-full p-3 text-center text-sm text-primary-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Load more lessons…
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
